/**
 * Business Capability Detail Route
 *
 * Displays comprehensive capability metadata with edit/delete actions
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useCapability } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  StatusBadge,
  ErrorBoundary,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteCapability, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';
import './$capabilityId.css';

export const Route = createFileRoute('/capabilities/$capabilityId')({
  component: CapabilityDetailPage,
});

function CapabilityDetailPage(): JSX.Element {
  const { capabilityId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditRoute = location.pathname.endsWith('/edit');

  const { data, loading, error: fetchError } = useCapability(capabilityId);
  const capability = data?.capability;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (loading) {
    return (
      <div className="capability-detail-page">
        <DetailSkeleton cards={4} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="capability-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load capability"
          description={fetchError.message}
          actionLabel="Back to Business Capabilities"
          onAction={() => navigate({ to: '/capabilities', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  if (!capability) {
    return (
      <div className="capability-detail-page">
        <EmptyState
          icon="search_off"
          title="Capability not found"
          description={`No capability found with ID: ${capabilityId}`}
          actionLabel="Back to Business Capabilities"
          onAction={() => navigate({ to: '/capabilities', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleEdit = (): void => {
    navigate({
      to: '/capabilities/$capabilityId/edit',
      params: { capabilityId: capability.entityId },
    });
  };

  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      await deleteCapability(capabilityId, false);
      await apolloClient.refetchQueries({ include: ['ListCapabilities'] });
      navigate({ to: '/capabilities', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteDialogOpen(false);
  };

  if (isEditRoute) {
    return (
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="capability-detail-page">
        <div className="breadcrumb">
          <Link to="/capabilities" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
            <IconElement name="arrow_back" aria-hidden="true" />
            <span>Back to Business Capabilities</span>
          </Link>
        </div>

        {error && (
          <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
            <div className="banner-content">
              <IconElement name="error" aria-hidden="true" />
              <span>{error}</span>
            </div>
          </BannerElement>
        )}

        <div className="capability-header">
          <div className="capability-header-content">
            <h1 className="forge-typography--headline4">{capability.name}</h1>
            <p className="forge-typography--body2 capability-subheading">
              Level {capability.level}
              {capability.parentCapabilityId && ` · Parent: ${capability.parentCapabilityId}`}
            </p>
          </div>
          <div className="capability-actions">
            <PermissionGate permission="capability:update">
              <ForgeButton type="button" variant="outlined" onClick={handleEdit}>
                <forge-icon name="edit" aria-hidden="true"></forge-icon>
                <span>Edit</span>
              </ForgeButton>
            </PermissionGate>
            <PermissionGate permission="capability:delete">
              <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
                <forge-icon name="delete" aria-hidden="true"></forge-icon>
                <span>Delete</span>
              </ForgeButton>
            </PermissionGate>
          </div>
        </div>

        <div className="capability-content">
          {/* Basic Information Card */}
          <CardElement className="capability-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Basic Information</h2>
            </div>
            <DividerElement />
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Entity ID</span>
                <span className="detail-value detail-value-code">{capability.entityId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Level</span>
                <span className="detail-value">{capability.level}</span>
              </div>
              {capability.description && (
                <div className="detail-row">
                  <span className="detail-label">Description</span>
                  <span className="detail-value">{capability.description}</span>
                </div>
              )}
              {capability.parentCapabilityId && (
                <div className="detail-row">
                  <span className="detail-label">Parent Capability</span>
                  <span className="detail-value detail-value-code">
                    {capability.parentCapabilityId}
                  </span>
                </div>
              )}
              {capability.tags.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Tags</span>
                  <span className="detail-value">
                    <div className="tag-list">
                      {capability.tags.map((tag) => (
                        <span key={tag} className="tag-chip">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </span>
                </div>
              )}
            </div>
          </CardElement>

          {/* Classification Card */}
          <CardElement className="capability-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Classification</h2>
            </div>
            <DividerElement />
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Criticality</span>
                <span className="detail-value">
                  <StatusBadge value={capability.criticality} variant="lifecycle" />
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Strategic Importance</span>
                <span className="detail-value">
                  <StatusBadge value={capability.strategicImportance} variant="lifecycle" />
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Lifecycle Status</span>
                <span className="detail-value">
                  <StatusBadge value={capability.lifecycleStatus} variant="lifecycle" />
                </span>
              </div>
              {capability.investmentPriority && (
                <div className="detail-row">
                  <span className="detail-label">Investment Priority</span>
                  <span className="detail-value">
                    <StatusBadge value={capability.investmentPriority} variant="lifecycle" />
                  </span>
                </div>
              )}
              {capability.maturityLevel != null && (
                <div className="detail-row">
                  <span className="detail-label">Maturity Level</span>
                  <span className="detail-value">{capability.maturityLevel} / 5</span>
                </div>
              )}
              {capability.targetMaturityLevel != null && (
                <div className="detail-row">
                  <span className="detail-label">Target Maturity Level</span>
                  <span className="detail-value">{capability.targetMaturityLevel} / 5</span>
                </div>
              )}
            </div>
          </CardElement>

          {/* Ownership Card */}
          <CardElement className="capability-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Ownership</h2>
            </div>
            <DividerElement />
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Business Owner</span>
                <span className="detail-value">{capability.businessOwnerId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Owning Business Unit</span>
                <span className="detail-value">{capability.owningBusinessUnitId}</span>
              </div>
              {capability.stakeholderIds.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Stakeholders</span>
                  <span className="detail-value">
                    <div className="tag-list">
                      {capability.stakeholderIds.map((id) => (
                        <span key={id} className="tag-chip">
                          {id}
                        </span>
                      ))}
                    </div>
                  </span>
                </div>
              )}
            </div>
          </CardElement>

          {/* Supporting Services Card (issue #1495) */}
          {capability.supportingServices && capability.supportingServices.length > 0 && (
            <CardElement className="capability-card">
              <div className="card-header">
                <h2 className="forge-typography--headline6">
                  Supporting Services
                  <span className="capability-service-count"> ({capability.serviceCount})</span>
                </h2>
              </div>
              <DividerElement />
              <div className="card-content">
                <div className="tag-list" aria-label="Supporting services">
                  {capability.supportingServices.map((service) => (
                    <Link
                      key={service.entityId}
                      to="/services/$serviceId"
                      params={{ serviceId: service.entityId }}
                      className="tag-chip service-chip-link"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </CardElement>
          )}

          {/* Investment Card */}
          {(capability.currentInvestmentLevel ||
            capability.targetInvestmentLevel ||
            capability.planningHorizon) && (
            <CardElement className="capability-card">
              <div className="card-header">
                <h2 className="forge-typography--headline6">Investment</h2>
              </div>
              <DividerElement />
              <div className="card-content">
                {capability.currentInvestmentLevel && (
                  <div className="detail-row">
                    <span className="detail-label">Current Investment Level</span>
                    <span className="detail-value">
                      <StatusBadge value={capability.currentInvestmentLevel} variant="lifecycle" />
                    </span>
                  </div>
                )}
                {capability.targetInvestmentLevel && (
                  <div className="detail-row">
                    <span className="detail-label">Target Investment Level</span>
                    <span className="detail-value">
                      <StatusBadge value={capability.targetInvestmentLevel} variant="lifecycle" />
                    </span>
                  </div>
                )}
                {capability.planningHorizon && (
                  <div className="detail-row">
                    <span className="detail-label">Planning Horizon</span>
                    <span className="detail-value">{capability.planningHorizon}</span>
                  </div>
                )}
              </div>
            </CardElement>
          )}

          {/* Child Capabilities Card (issue #1465) */}
          {capability.children && capability.children.length > 0 && (
            <CardElement className="capability-card">
              <div className="card-header">
                <h2 className="forge-typography--headline6">Child Capabilities</h2>
              </div>
              <DividerElement />
              <div className="card-content">
                <ul className="capability-children-list" aria-label="Child capabilities">
                  {capability.children.map((child) => (
                    <li key={child.entityId} className="capability-children-list-item">
                      <Link
                        to="/capabilities/$capabilityId"
                        params={{ capabilityId: child.entityId }}
                        className="capability-child-link"
                      >
                        {child.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </CardElement>
          )}

          {/* Additional Information Card */}
          {(capability.notes || capability.assumptions) && (
            <CardElement className="capability-card">
              <div className="card-header">
                <h2 className="forge-typography--headline6">Additional Information</h2>
              </div>
              <DividerElement />
              <div className="card-content">
                {capability.notes && (
                  <div className="detail-row">
                    <span className="detail-label">Notes</span>
                    <span className="detail-value detail-value-multiline">{capability.notes}</span>
                  </div>
                )}
                {capability.assumptions && (
                  <div className="detail-row">
                    <span className="detail-label">Assumptions</span>
                    <span className="detail-value detail-value-multiline">
                      {capability.assumptions}
                    </span>
                  </div>
                )}
              </div>
            </CardElement>
          )}

          {/* Metadata Card */}
          <CardElement className="capability-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Metadata</h2>
            </div>
            <DividerElement />
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {formatDate(capability._createdAt)} by {capability._createdBy}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">
                  {formatDate(capability._updatedAt)} by {capability._updatedBy}
                </span>
              </div>
              {capability._deletedAt && (
                <div className="detail-row">
                  <span className="detail-label">Deleted</span>
                  <span className="detail-value detail-value-deleted">
                    {formatDate(capability._deletedAt)}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Schema Version</span>
                <span className="detail-value">{capability._schemaVersion}</span>
              </div>
            </div>
          </CardElement>
        </div>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title="Delete Capability"
          message="Are you sure you want to delete this capability?"
          itemName={capability.name}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDeleting={isDeleting}
        />
      </div>
    </ErrorBoundary>
  );
}

export { CapabilityDetailPage };
